import type { AxiosResponse } from 'axios';

/**
 * AGraphWrappedType models a response payload that may be either the raw
 * value (`T`) or wrapped under a known field name (`WrapperField`).
 *
 * @example `const response = await axios.get<AGraphWrappedType<Foo, 'bar'>>(path);`
 * where response will be either type `Foo` or `{ bar: Foo }`:
 */
export type AGraphWrappedType<T, WrapperField extends string> = T | Record<WrapperField, T>;

/**
 * Unwraps a possibly-wrapped Axios response so `response.data` is always of type `ResponseType`.
 * If the payload is `{ [field]: ResponseType }`, it extracts `ResponseType`; otherwise it returns the
 * payload as-is. All Axios metadata (status, headers, etc.) is preserved.
 *
 * @param resp - AxiosResponse whose `data` may be `ResponseType` or `{ [field]: ResponseType }`.
 * @param field - The wrapper field key to check for
 * @returns AxiosResponse<ResponseType>
 * @example `unwrapAGraphResponse<Foo, 'bar'>(response, 'bar');`
 */
export function unwrapAGraphResponse<ResponseType, WrapperField extends string>(
  resp: AxiosResponse<AGraphWrappedType<ResponseType, WrapperField>>,
  field: WrapperField
): AxiosResponse<ResponseType> {
  const d = resp.data;
  const value =
    typeof d === 'object' && d !== null && field in (d as Record<WrapperField, unknown>)
      ? (d as Record<WrapperField, ResponseType>)[field]
      : (d as ResponseType);
  return {
    ...resp,
    data: value,
  } as AxiosResponse<ResponseType>;
}

/**
 * Wraps a request payload under the provided field: `{ [field]: data } if shouldWrap is true`.
 * Otherwise just return the original payload.
 *
 * @param data - The payload of type `RequestType` that may be wrapped
 * @param field - The wrapper field name
 * @param shouldWrap - Whether `data` should be wrapped in `field`
 * @returns the possibly-wrapped payload, `Record<WrapperField, RequestType>`
 *
 * @example `axios.post('/foo', wrapAGraphRequest(payload, 'bar', true));`
 * will return `{ bar: payload }`
 */
// Overloads for better type inference based on `shouldWrap`
export function wrapAGraphRequest<RequestType, WrapperField extends string>(
  data: RequestType,
  field: WrapperField,
  shouldWrap: true
): Record<WrapperField, RequestType>;

export function wrapAGraphRequest<RequestType>(data: RequestType, field: string, shouldWrap: false): RequestType;

export function wrapAGraphRequest<RequestType, WrapperField extends string>(
  data: RequestType,
  field: WrapperField,
  shouldWrap: boolean
): AGraphWrappedType<RequestType, WrapperField>;

export function wrapAGraphRequest<RequestType, WrapperField extends string>(
  data: RequestType,
  field: WrapperField,
  shouldWrap = false
): AGraphWrappedType<RequestType, WrapperField> {
  if (shouldWrap) {
    return { [field]: data } as Record<WrapperField, RequestType>;
  }
  return data;
}
